import React, { useState, useEffect } from 'react';
import { Star } from 'lucide-react';

const StarRating = ({ rating, size = 'h-5 w-5' }) => (
  <div className="flex gap-0.5">
    {[1, 2, 3, 4, 5].map((star) => (
      <Star
        key={star}
        className={size}
        fill={star <= rating ? '#FF8C19' : 'none'}
        style={{ color: star <= rating ? '#FF8C19' : '#e5d5c0' }}
      />
    ))}
  </div>
);

const RatingBar = ({ label, count, total }) => {
  const pct = total > 0 ? (count / total) * 100 : 0;
  return (
    <div className="flex items-center gap-2 text-sm">
      <span className="w-12 text-right" style={{ color: '#4a2c1a' }}>{label}</span>
      <div className="flex-1 h-2 rounded-full overflow-hidden" style={{ backgroundColor: '#e5d5c0' }}>
        <div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: '#FF8C19' }} />
      </div>
      <span className="w-8 text-xs" style={{ color: '#736c64' }}>{count}</span>
    </div>
  );
};

const ReviewCard = ({ review }) => {
  const date = new Date(review.createdAt).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  const initials = review.customerName.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);

  return (
    <div className="bg-white rounded-lg shadow-md p-6 border-l-4 transition-all hover:shadow-lg" style={{ borderColor: '#FF8C19' }}>
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm text-white" style={{ backgroundColor: '#4a2c1a' }}>
            {initials}
          </div>
          <div>
            <p className="font-bold" style={{ color: '#4a2c1a' }}>{review.customerName}</p>
            <p className="text-xs" style={{ color: '#736c64' }}>{date}</p>
          </div>
        </div>
        <StarRating rating={review.rating} size="h-4 w-4" />
      </div>

      {review.title && (
        <h3 className="font-bold mb-2" style={{ color: '#4a2c1a' }}>{review.title}</h3>
      )}

      <p className="text-sm leading-relaxed mb-3" style={{ color: '#4a2c1a' }}>{review.text}</p>

      {review.photos && review.photos.length > 0 && (
        <div className="flex gap-2 flex-wrap">
          {review.photos.map((photo, i) => (
            <img
              key={i}
              src={photo}
              alt={`Review photo ${i + 1}`}
              className="w-24 h-24 object-cover rounded-lg border-2 cursor-pointer hover:opacity-90 transition-opacity"
              style={{ borderColor: '#e5d5c0' }}
              onClick={() => window.open(photo, '_blank')}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const Reviews = () => {
  const [reviews, setReviews] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      fetch('/api/reviews/approved').then(r => r.json()),
      fetch('/api/reviews/stats').then(r => r.json())
    ]).then(([reviewsData, statsData]) => {
      setReviews(reviewsData);
      setStats(statsData);
    }).catch(() => {})
    .finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#f7f5f2' }}>
      <p style={{ color: '#736c64' }}>Loading reviews...</p>
    </div>
  );

  return (
    <div className="min-h-screen py-12" style={{ backgroundColor: '#f7f5f2' }}>
      <div className="container mx-auto px-4 max-w-4xl">
        <div className="text-center mb-10">
          <h1 className="text-4xl md:text-5xl font-bold mb-3" style={{ color: '#4a2c1a', fontFamily: 'Georgia, serif' }}>
            Customer Reviews
          </h1>
          <p className="text-base" style={{ color: '#736c64' }}>See what our maple lovers are saying</p>
        </div>

        {/* Stats Summary */}
        {stats && stats.totalReviews > 0 && (
          <div className="bg-white rounded-lg shadow-lg p-8 mb-8">
            <div className="flex flex-col md:flex-row items-center gap-8">
              <div className="text-center md:border-r md:pr-8" style={{ borderColor: '#e5d5c0' }}>
                <p className="text-5xl font-bold" style={{ color: '#FF8C19' }}>{stats.avgRating}</p>
                <StarRating rating={Math.round(stats.avgRating)} />
                <p className="text-sm mt-1" style={{ color: '#736c64' }}>{stats.totalReviews} review{stats.totalReviews !== 1 ? 's' : ''}</p>
              </div>
              <div className="flex-1 space-y-2 w-full">
                <RatingBar label="5 stars" count={stats.fiveStars} total={stats.totalReviews} />
                <RatingBar label="4 stars" count={stats.fourStars} total={stats.totalReviews} />
                <RatingBar label="3 stars" count={stats.threeStars} total={stats.totalReviews} />
                <RatingBar label="2 stars" count={stats.twoStars} total={stats.totalReviews} />
                <RatingBar label="1 star" count={stats.oneStars} total={stats.totalReviews} />
              </div>
            </div>
          </div>
        )}

        {/* Reviews List */}
        {reviews.length === 0 ? (
          <div className="bg-white rounded-lg shadow-md p-12 text-center">
            <Star className="h-16 w-16 mx-auto mb-4" style={{ color: '#e5d5c0' }} />
            <h2 className="text-2xl font-bold mb-2" style={{ color: '#4a2c1a' }}>No Reviews Yet</h2>
            <p style={{ color: '#736c64' }}>Be the first to share your experience with our maple syrup!</p>
          </div>
        ) : (
          <div className="space-y-4">
            {reviews.map((review) => (
              <ReviewCard key={review.id} review={review} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Reviews;
